const defaultStoreIdMappings: Record<string, string> = {
  "com.soaak.app.sleep.annual": "Sleep Well Annual",
  "com.soaak.app.sleep.monthly": "Sleep Well Monthly",
  "com.soaak.app.premium.annualplan": "Full Access Annual",
  "com.soaak.app.premium": "Full Access Monthly",
  "google.sleep.annual": "Sleep Well Annual",
  "google.sleep.monthly": "Sleep Well Monthly",
  "google.premium.annual": "Full Access Annual",
  "google.premium.monthly": "Full Access Monthly",
  // API may return I/l transposition vs older Stripe copy-paste (lnIR vs InlR).
  "price_1Q3XCRI7OMFn2tF6f6rVlnIR": "Sleep Well Annual",
  "price_1Q3XCRI7OMFn2tF6f6rVInlR": "Sleep Well Annual",
  "price_1Q3X8RI7OMFn2tF65A4rmkXc": "Sleep Well Monthly",
  "price_1UAYbII7OMFn2tF6PgRSrJ9h": "Sleep Well Monthly",
  "price_1UAYctI7OMFn2tF6JaO2L1Dv": "Sleep Well Monthly",
  "price_1MMbV0I7OMFn2tF6xel4rAtw": "Full Access Annual",
  // Alternate annual Stripe price id from API — confirm with product if label differs.
  "price_1SKmL9I7OMFn2tF64dquD1zN": "Full Access Annual",
  "price_1KOBVUI7OMFn2tF6OShBdZUN": "Full Access Monthly",
  "price_1KOBVUI7OMFn2tF6OSHBdZUN": "Full Access Monthly",
};

const paidTrialPriceIds = {
  Annually: process.env.REACT_APP_PAID_TRIAL_ANNUAL_PRICE_ID || '',
  Monthly: process.env.REACT_APP_PAID_TRIAL_MONTHLY_PRICE_ID || '',
};

const parseStoreIdMappings = (): Record<string, string> => {
  const storeIdMappingsJson = process.env.REACT_APP_STORE_ID_MAPPINGS_JSON;
  const paidTrialStoreIdMappings = {
    ...(paidTrialPriceIds.Annually
      ? { [paidTrialPriceIds.Annually]: 'Full Access Annual' }
      : {}),
    ...(paidTrialPriceIds.Monthly
      ? { [paidTrialPriceIds.Monthly]: 'Full Access Monthly' }
      : {}),
  };

  if (!storeIdMappingsJson) {
    return {
      ...defaultStoreIdMappings,
      ...paidTrialStoreIdMappings,
    };
  }

  try {
    const parsedStoreIdMappings = JSON.parse(storeIdMappingsJson);
    if (
      parsedStoreIdMappings &&
      typeof parsedStoreIdMappings === 'object' &&
      !Array.isArray(parsedStoreIdMappings)
    ) {
      return {
        ...defaultStoreIdMappings,
        ...(parsedStoreIdMappings as Record<string, string>),
        ...paidTrialStoreIdMappings,
      };
    }
  } catch (error) {
    // Fall back to default production mappings if env value is invalid.
  }

  return {
    ...defaultStoreIdMappings,
    ...paidTrialStoreIdMappings,
  };
};

const storeIdMappings = parseStoreIdMappings();

const sleepWellEntitlementSlugs = [
  'sleep-well-frequency-annually',
  'sleep-well-frequency-monthly',
  'sleep-well-paid-ads-monthly',
];

const sleepWellPaidAdsEntitlementSlugs = [
  'sleep-well-paid-ads-monthly',
];

const sleepWellPaidAdsStoreIds = [
  'price_1UAYbII7OMFn2tF6PgRSrJ9h',
  'price_1UAYctI7OMFn2tF6JaO2L1Dv',
];

const isSleepWellPaidAdsEntitlement = (entitlement?: any): boolean => {
  if (!entitlement) {
    return false;
  }

  if (
    entitlement.slug &&
    sleepWellPaidAdsEntitlementSlugs.includes(entitlement.slug)
  ) {
    return true;
  }

  const storeId = entitlement.storeID || entitlement.storeId;
  return sleepWellPaidAdsStoreIds.includes(storeId);
};

const isSleepWellEntitlement = (entitlement?: any): boolean => {
  if (!entitlement) {
    return false;
  }

  if (
    entitlement.slug &&
    sleepWellEntitlementSlugs.includes(entitlement.slug)
  ) {
    return true;
  }

  if (
    entitlement.title &&
    String(entitlement.title).startsWith('Sleep Well')
  ) {
    return true;
  }

  const storeId = entitlement.storeID || entitlement.storeId;
  return storeIdMappings[storeId]?.includes('Sleep Well') || false;
};

const config = {
  backendUrl: process.env.REACT_APP_BACKEND_URL || '',
  apiDocumentationUrl: process.env.REACT_APP_API_DOCUMENTATION_URL || '',
  frontendUrl: {
    host: process.env.REACT_APP_FRONTEND_HOST || '',
    protocol: process.env.REACT_APP_FRONTEND_PROTOCOL || 'https',
  },
  tenantMode: process.env.REACT_APP_TENANT_MODE || 'single',
  isPlanEnabled: process.env.REACT_APP_IS_PLAN_ENABLED === 'true',
  stripePublishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
  pixelId: process.env.REACT_APP_PIXEL_ID,
  impactUniversalTrackingTagUrl:
    process.env.REACT_APP_IMPACT_UTT_URL || '',
  impactReferralWindowDays: Number(
    process.env.REACT_APP_IMPACT_REFERRAL_WINDOW_DAYS || 30,
  ),
  marketingTermsOrServiceUrl: process.env.REACT_APP_MARKETING_TERMS_URL || '',
  marketingPrivacyPolicyUrl: process.env.REACT_APP_MARKETING_PRIVACY_URL || '',
  marketingReferAFriendUrl: process.env.REACT_APP_MARKETING_REFER_URL || '',
  marketingPricingUrl: process.env.REACT_APP_MARKETING_PRICING_URL || '',
  paidTrialPriceIds,
  storeIdMappings,
  sleepWellEntitlementSlugs,
  sleepWellPaidAdsEntitlementSlugs,
  sleepWellPaidAdsStoreIds,
  isSleepWellEntitlement,
  isSleepWellPaidAdsEntitlement,
  decodePlanFromStoreId: (storeId: string) =>
    storeIdMappings[storeId] || 'Unknown Plan',
};

export default config;
